import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarComponent } from './ui/calendar';
import MobileFriendlyDatePicker from './MobileFriendlyDatePicker';
import { 
  Users, 
  GraduationCap, 
  FileText, 
  BarChart3, 
  Clock,
  Trash2,
  Activity,
  Calendar,
  Mail,
  Building,
  LogOut,
  Shield,
  MessageSquare,
  Edit,
  CalendarIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [systemLogs, setSystemLogs] = useState(null);
  const [allPupils, setAllPupils] = useState([]);
  const [allReflectionLogs, setAllReflectionLogs] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit dialog states
  const [editInstructorDialog, setEditInstructorDialog] = useState(false);
  const [editPupilDialog, setEditPupilDialog] = useState(false);
  const [editQuestionDialog, setEditQuestionDialog] = useState(false);
  const [editLogDialog, setEditLogDialog] = useState(false);
  
  // Current editing items
  const [currentInstructor, setCurrentInstructor] = useState(null);
  const [currentPupil, setCurrentPupil] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentLog, setCurrentLog] = useState(null);
  
  // Form data states
  const [instructorFormData, setInstructorFormData] = useState({
    name: '', email: '', school_name: '', phone: ''
  });
  const [pupilFormData, setPupilFormData] = useState({
    name: '', email: '', phone: '', license_number: '',
    test_booked: false, test_date: null, test_time: '', additional_needs: '', notes: ''
  });
  const [questionFormData, setQuestionFormData] = useState({
    text: '', question_type: 'text', scale_min: 1, scale_max: 10
  });
  const [logFormData, setLogFormData] = useState({
    lesson_date: null, lesson_hours: 1.0, notes: ''
  });

  useEffect(() => {
    fetchDashboardData();
    fetchInstructors();
    fetchSystemLogs();
    fetchAllPupils();
    fetchAllReflectionLogs();
    fetchAllQuestions();
  }, []); // Fetch functions are stable (defined in component scope)

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    }
  };

  const fetchInstructors = async () => {
    try {
      const response = await axios.get(`${API}/admin/instructors`);
      setInstructors(response.data);
    } catch (error) {
      console.error('Error fetching instructors:', error);
      toast.error('Failed to load instructors');
    }
  };

  const fetchSystemLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/system-logs`);
      setSystemLogs(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching system logs:', error);
      toast.error('Failed to load system logs');
      setLoading(false);
    }
  };

  const fetchAllPupils = async () => {
    try {
      const response = await axios.get(`${API}/admin/pupils`);
      setAllPupils(response.data);
    } catch (error) {
      console.error('Error fetching pupils:', error);
      toast.error('Failed to load pupils');
    }
  };

  const fetchAllReflectionLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/reflection-logs`);
      setAllReflectionLogs(response.data);
    } catch (error) {
      console.error('Error fetching reflection logs:', error);
      toast.error('Failed to load reflection logs');
    }
  };

  const fetchAllQuestions = async () => {
    try {
      const response = await axios.get(`${API}/admin/questions`);
      setAllQuestions(response.data);
    } catch (error) {
      console.error('Error fetching questions:', error);
      toast.error('Failed to load questions');
    }
  };

  const handleDeleteInstructor = async (instructorId, instructorName) => {
    if (!window.confirm(`⚠️ WARNING: This will permanently delete instructor "${instructorName}" and all their associated data. This action cannot be undone. Are you sure?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/instructors/${instructorId}`);
      toast.success(`Instructor "${instructorName}" deleted successfully`);
      fetchInstructors();
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting instructor:', error);
      toast.error('Failed to delete instructor');
    }
  };

  const handleDeletePupil = async (pupilId, pupilName) => {
    if (!window.confirm(`⚠️ WARNING: This will permanently delete pupil "${pupilName}" and all their reflection logs. This action cannot be undone. Are you sure?`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/admin/pupils/${pupilId}`);
      toast.success(`${response.data.message}`);
      fetchAllPupils();
      fetchDashboardData();
      fetchAllReflectionLogs();
    } catch (error) {
      console.error('Error deleting pupil:', error);
      toast.error('Failed to delete pupil');
    }
  };

  const handleDeleteReflectionLog = async (logId, pupilName, lessonDate) => {
    if (!window.confirm(`⚠️ Delete reflection log for "${pupilName}" from ${new Date(lessonDate).toLocaleDateString()}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/reflection-logs/${logId}`);
      toast.success('Reflection log deleted successfully');
      fetchAllReflectionLogs();
      fetchDashboardData();
      fetchAllPupils();
    } catch (error) {
      console.error('Error deleting reflection log:', error);
      toast.error('Failed to delete reflection log');
    }
  };

  const handleDeleteQuestion = async (questionId, questionText) => {
    if (!window.confirm(`⚠️ WARNING: This will permanently delete the question "${questionText}". This action cannot be undone. Are you sure?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/questions/${questionId}`);
      toast.success('Question deleted successfully');
      fetchAllQuestions();
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete question');
    }
  };

  const handleClearAllLogs = async () => {
    if (!window.confirm('⚠️ DANGER: This will delete ALL reflection logs system-wide. Type "DELETE ALL LOGS" to confirm:') || 
        window.prompt('Type "DELETE ALL LOGS" to confirm:') !== 'DELETE ALL LOGS') {
      toast.info('Operation cancelled');
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/system/clear-all-logs`);
      toast.success(`${response.data.message} (${response.data.deleted_count} logs deleted)`);
      fetchAllReflectionLogs();
      fetchDashboardData();
      fetchAllPupils();
    } catch (error) {
      console.error('Error clearing all logs:', error);
      toast.error('Failed to clear all logs');
    }
  };

  const handleClearAllPupils = async () => {
    if (!window.confirm('⚠️ EXTREME DANGER: This will delete ALL pupils and ALL logs system-wide. Type "DELETE ALL PUPILS" to confirm:') || 
        window.prompt('Type "DELETE ALL PUPILS" to confirm:') !== 'DELETE ALL PUPILS') {
      toast.info('Operation cancelled');
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/system/clear-all-pupils`);
      toast.success(`${response.data.message} (${response.data.deleted_pupils} pupils, ${response.data.deleted_logs} logs deleted)`);
      fetchAllPupils();
      fetchAllReflectionLogs();
      fetchDashboardData();
    } catch (error) {
      console.error('Error clearing all pupils:', error);
      toast.error('Failed to clear all pupils');
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Admin logged out successfully');
  };

  // Edit Instructor Functions
  const handleEditInstructor = (instructor) => {
    setCurrentInstructor(instructor);
    setInstructorFormData({
      name: instructor.name || '',
      email: instructor.email || '',
      school_name: instructor.school_name || '',
      phone: instructor.phone || ''
    });
    setEditInstructorDialog(true);
  };

  const handleUpdateInstructor = async (e) => {
    e.preventDefault();
    if (!instructorFormData.name.trim() || !instructorFormData.email.trim()) {
      toast.error('Name and email are required');
      return;
    }

    try {
      await axios.patch(`${API}/admin/instructors/${currentInstructor.id}`, instructorFormData);
      toast.success('Instructor updated successfully');
      setEditInstructorDialog(false);
      fetchInstructors();
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating instructor:', error);
      toast.error('Failed to update instructor');
    }
  };

  // Edit Pupil Functions
  const handleEditPupil = (pupil) => {
    setCurrentPupil(pupil);
    setPupilFormData({
      name: pupil.name || '',
      email: pupil.email || '',
      phone: pupil.phone || '',
      license_number: pupil.license_number || '',
      test_booked: pupil.test_booked || false,
      test_date: pupil.test_date ? new Date(pupil.test_date) : null,
      test_time: pupil.test_time || '',
      additional_needs: pupil.additional_needs || '',
      notes: pupil.notes || ''
    });
    setEditPupilDialog(true);
  };

  const handleUpdatePupil = async (e) => {
    e.preventDefault();
    if (!pupilFormData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    if (pupilFormData.test_booked && !pupilFormData.test_date) {
      toast.error('Test date is required when test is booked');
      return;
    }

    if (pupilFormData.test_booked && !pupilFormData.test_time) {
      toast.error('Test time is required when test is booked');
      return;
    }

    try {
      const dataToSend = {
        ...pupilFormData,
        test_date: pupilFormData.test_date ? pupilFormData.test_date.toISOString() : null,
        test_time: pupilFormData.test_time || null
      };
      
      await axios.put(`${API}/admin/pupils/${currentPupil.id}`, dataToSend);
      toast.success('Pupil updated successfully');
      setEditPupilDialog(false);
      fetchAllPupils();
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating pupil:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to update pupil');
      }
    }
  };

  // Edit Question Functions
  const handleEditQuestion = (question) => {
    setCurrentQuestion(question);
    setQuestionFormData({
      text: question.text || '',
      question_type: question.question_type || 'text',
      scale_min: question.scale_min || 1,
      scale_max: question.scale_max || 10
    });
    setEditQuestionDialog(true);
  };

  const handleUpdateQuestion = async (e) => {
    e.preventDefault();
    if (!questionFormData.text.trim()) {
      toast.error('Question text is required');
      return;
    }

    if (questionFormData.question_type === 'scale' && (questionFormData.scale_min >= questionFormData.scale_max)) {
      toast.error('Scale minimum must be less than maximum');
      return;
    }

    try {
      // Note: We'll need to add a PUT endpoint for questions in the backend
      await axios.put(`${API}/admin/questions/${currentQuestion.id}`, questionFormData);
      toast.success('Question updated successfully');
      setEditQuestionDialog(false);
      fetchAllQuestions();
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating question:', error);
      toast.error('Failed to update question - Update endpoint may not be implemented yet');
    }
  };

  // Edit Reflection Log Functions
  const handleEditLog = (log) => {
    setCurrentLog(log);
    setLogFormData({
      lesson_date: log.lesson_date ? new Date(log.lesson_date) : null,
      lesson_hours: log.lesson_hours || 1.0,
      notes: log.notes || ''
    });
    setEditLogDialog(true);
  };

  const handleUpdateLog = async (e) => {
    e.preventDefault();
    if (!logFormData.lesson_date) {
      toast.error('Lesson date is required');
      return;
    }

    try {
      const dataToSend = {
        ...logFormData,
        lesson_date: logFormData.lesson_date.toISOString()
      };
      
      // Note: We'll need to add a PUT endpoint for reflection logs in the backend
      await axios.put(`${API}/admin/reflection-logs/${currentLog.id}`, dataToSend);
      toast.success('Reflection log updated successfully');
      setEditLogDialog(false);
      fetchAllReflectionLogs();
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating reflection log:', error);
      toast.error('Failed to update reflection log - Update endpoint may not be implemented yet');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Admin Header */}
      <header className="bg-red-600 dark:bg-red-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Shield size={32} />
              <div>
                <h1 className="text-2xl font-bold">ReflectLogs Admin</h1>
                <p className="text-red-100">System Administration Dashboard</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="font-medium">{user?.name}</p>
                <p className="text-sm text-red-100">Administrator</p>
              </div>
              <ThemeToggle variant="outline" className="text-white border-white hover:bg-red-500 dark:hover:bg-red-600" />
              <Button variant="outline" size="sm" onClick={handleLogout} className="text-white border-white hover:bg-red-500 dark:hover:bg-red-600">
                <LogOut size={16} className="mr-1" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-7 mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="instructors">Instructors</TabsTrigger>
            <TabsTrigger value="pupils">All Pupils</TabsTrigger>
            <TabsTrigger value="logs">All Logs</TabsTrigger>
            <TabsTrigger value="questions">All Questions</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="system">System</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-slate-800">System Overview</h2>
              
              {/* System Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-blue-800 flex items-center">
                      <GraduationCap className="mr-2" size={20} />
                      Instructors
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-blue-900">
                      {dashboardData?.system_stats?.total_instructors || 0}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-emerald-800 flex items-center">
                      <Users className="mr-2" size={20} />
                      Total Pupils
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-emerald-900">
                      {dashboardData?.system_stats?.total_pupils || 0}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-amber-800 flex items-center">
                      <FileText className="mr-2" size={20} />
                      Total Logs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-amber-900">
                      {dashboardData?.system_stats?.total_logs || 0}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-purple-800 flex items-center">
                      <Clock className="mr-2" size={20} />
                      Total Hours
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-purple-900">
                      {dashboardData?.system_stats?.total_hours || 0}h
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-indigo-800 flex items-center">
                      <BarChart3 className="mr-2" size={20} />
                      Questions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-indigo-900">
                      {dashboardData?.system_stats?.total_questions || 0}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Instructor Performance Overview */}
              <Card>
                <CardHeader>
                  <CardTitle>Instructor Activity Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {dashboardData?.instructor_stats?.map((instructor) => (
                      <div key={instructor.id} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold text-slate-800">{instructor.name}</h4>
                            {instructor.school_name && (
                              <p className="text-sm text-slate-600">{instructor.school_name}</p>
                            )}
                          </div>
                          <Badge variant="secondary">{instructor.total_hours}h</Badge>
                        </div>
                        <div className="space-y-1 text-sm text-slate-600">
                          <p>📧 {instructor.email}</p>
                          <p>👥 {instructor.pupils_count} pupils</p>
                          <p>📝 {instructor.logs_count} logs</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Instructors Tab */}
          <TabsContent value="instructors">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-3xl font-bold text-slate-800">Instructor Management</h2>
                <Badge variant="outline" className="bg-blue-50 text-blue-700">
                  {instructors.length} Total Instructors
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {instructors.map((instructor) => (
                  <Card key={instructor.id} className="relative">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>{instructor.name}</span>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditInstructor(instructor)}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteInstructor(instructor.id, instructor.name)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Mail size={14} className="text-slate-500" />
                          <span className="text-sm">{instructor.email}</span>
                        </div>
                        {instructor.school_name && (
                          <div className="flex items-center space-x-2">
                            <Building size={14} className="text-slate-500" />
                            <span className="text-sm">{instructor.school_name}</span>
                          </div>
                        )}
                        <div className="flex items-center space-x-2">
                          <Calendar size={14} className="text-slate-500" />
                          <span className="text-sm">
                            Joined: {new Date(instructor.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Edit Instructor Dialog */}
          <Dialog open={editInstructorDialog} onOpenChange={setEditInstructorDialog}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Edit Instructor</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateInstructor} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="instructor-name">Name *</Label>
                  <Input
                    id="instructor-name"
                    value={instructorFormData.name}
                    onChange={(e) => setInstructorFormData({...instructorFormData, name: e.target.value})}
                    placeholder="Instructor name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="instructor-email">Email *</Label>
                  <Input
                    id="instructor-email"
                    type="email"
                    value={instructorFormData.email}
                    onChange={(e) => setInstructorFormData({...instructorFormData, email: e.target.value})}
                    placeholder="instructor@example.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="instructor-school">School Name</Label>
                  <Input
                    id="instructor-school"
                    value={instructorFormData.school_name}
                    onChange={(e) => setInstructorFormData({...instructorFormData, school_name: e.target.value})}
                    placeholder="School or company name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="instructor-phone">Phone</Label>
                  <Input
                    id="instructor-phone"
                    value={instructorFormData.phone}
                    onChange={(e) => setInstructorFormData({...instructorFormData, phone: e.target.value})}
                    placeholder="Phone number"
                  />
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setEditInstructorDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    Update Instructor
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-slate-800">Recent System Activity</h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Reflection Logs */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Activity className="mr-2" size={20} />
                      Recent Reflection Logs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {systemLogs?.recent_reflection_logs?.slice(0, 10).map((log, index) => (
                        <div key={log.id} className="flex justify-between items-center p-3 bg-slate-50 rounded">
                          <div>
                            <p className="text-sm font-medium">Pupil ID: {log.pupil_id.substring(0, 8)}...</p>
                            <p className="text-xs text-slate-600">
                              {new Date(log.lesson_date).toLocaleDateString()} • {log.lesson_hours}h
                            </p>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {new Date(log.created_at).toLocaleDateString()}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Pupils */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Users className="mr-2" size={20} />
                      Recent Pupils Added
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {systemLogs?.recent_pupils?.slice(0, 10).map((pupil) => (
                        <div key={pupil.id} className="flex justify-between items-center p-3 bg-slate-50 rounded">
                          <div>
                            <p className="text-sm font-medium">{pupil.name}</p>
                            <p className="text-xs text-slate-600">{pupil.email || 'No email'}</p>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {new Date(pupil.created_at).toLocaleDateString()}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* All Pupils Management Tab */}
          <TabsContent value="pupils">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-3xl font-bold text-slate-800">All Pupils Management</h2>
                <div className="flex space-x-2">
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">
                    {allPupils.length} Total Pupils
                  </Badge>
                  <Button
                    variant="destructive"
                    onClick={handleClearAllPupils}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    🗑️ Clear All Pupils
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {allPupils.map((pupil) => (
                  <Card key={pupil.id} className="relative">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>{pupil.name}</span>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditPupil(pupil)}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeletePupil(pupil.id, pupil.name)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {pupil.email && (
                          <div className="flex items-center space-x-2">
                            <Mail size={14} className="text-slate-500" />
                            <span className="text-sm">{pupil.email}</span>
                          </div>
                        )}
                        {pupil.license_number && (
                          <div className="flex items-center space-x-2">
                            <FileText size={14} className="text-slate-500" />
                            <span className="text-sm">License: {pupil.license_number}</span>
                          </div>
                        )}
                        <div className="flex items-center space-x-2">
                          <Calendar size={14} className="text-slate-500" />
                          <span className="text-sm">
                            Added: {new Date(pupil.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center mt-3">
                          <Badge variant="secondary">{pupil.logs_count} logs</Badge>
                          <Badge variant="outline" className="bg-purple-50 text-purple-700">
                            {pupil.total_hours}h
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {allPupils.length === 0 && (
                <Card className="text-center p-8">
                  <CardContent>
                    <Users size={48} className="mx-auto text-gray-400 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No pupils in system</h3>
                    <p className="text-gray-500">All pupils have been cleared or none have been added yet</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Edit Pupil Dialog */}
          <Dialog open={editPupilDialog} onOpenChange={setEditPupilDialog}>
            <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Pupil</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdatePupil} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="pupil-name">Name *</Label>
                  <Input
                    id="pupil-name"
                    value={pupilFormData.name}
                    onChange={(e) => setPupilFormData({...pupilFormData, name: e.target.value})}
                    placeholder="Pupil name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pupil-email">Email</Label>
                  <Input
                    id="pupil-email"
                    type="email"
                    value={pupilFormData.email}
                    onChange={(e) => setPupilFormData({...pupilFormData, email: e.target.value})}
                    placeholder="pupil@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pupil-phone">Phone</Label>
                  <Input
                    id="pupil-phone"
                    value={pupilFormData.phone}
                    onChange={(e) => setPupilFormData({...pupilFormData, phone: e.target.value})}
                    placeholder="Phone number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pupil-license">License Number</Label>
                  <Input
                    id="pupil-license"
                    value={pupilFormData.license_number}
                    onChange={(e) => setPupilFormData({...pupilFormData, license_number: e.target.value})}
                    placeholder="Driving license number"
                  />
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="pupil-test-booked"
                      checked={pupilFormData.test_booked}
                      onCheckedChange={(checked) => setPupilFormData({...pupilFormData, test_booked: checked})}
                    />
                    <Label htmlFor="pupil-test-booked">Test Booked</Label>
                  </div>
                  
                  {pupilFormData.test_booked && (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="pupil-test-date">Test Date *</Label>
                        <MobileFriendlyDatePicker
                          selected={pupilFormData.test_date}
                          onSelect={(date) => setPupilFormData({...pupilFormData, test_date: date})}
                          placeholder="Pick a date"
                        />
                      </div>
                      <div>
                        <Label htmlFor="pupil-test-time">Test Time *</Label>
                        <Input
                          id="pupil-test-time"
                          type="time"
                          value={pupilFormData.test_time}
                          onChange={(e) => setPupilFormData({...pupilFormData, test_time: e.target.value})}
                          className="w-full"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="pupil-additional-needs">Additional Needs</Label>
                    <Textarea
                      id="pupil-additional-needs"
                      value={pupilFormData.additional_needs}
                      onChange={(e) => setPupilFormData({...pupilFormData, additional_needs: e.target.value})}
                      placeholder="Enter any additional needs, or other important information..."
                      rows={3}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="pupil-notes">Notes</Label>
                    <Textarea
                      id="pupil-notes"
                      value={pupilFormData.notes}
                      onChange={(e) => setPupilFormData({...pupilFormData, notes: e.target.value})}
                      placeholder="Enter any additional notes..."
                      rows={3}
                    />
                  </div>
                </div>
                
                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setEditPupilDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    Update Pupil
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* All Reflection Logs Management Tab */}
          <TabsContent value="logs">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-3xl font-bold text-slate-800">All Reflection Logs Management</h2>
                <div className="flex space-x-2">
                  <Badge variant="outline" className="bg-amber-50 text-amber-700">
                    {allReflectionLogs.length} Total Logs
                  </Badge>
                  <Button
                    variant="destructive"
                    onClick={handleClearAllLogs}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    🗑️ Clear All Logs
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                {allReflectionLogs.map((log) => (
                  <Card key={log.id}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-800 mb-2">
                            {log.pupil_name}
                          </h4>
                          <div className="grid grid-cols-2 gap-4 text-sm text-slate-600">
                            <div>📅 Lesson: {new Date(log.lesson_date).toLocaleDateString()}</div>
                            <div>⏰ Hours: {log.lesson_hours}h</div>
                            <div>💬 Answers: {log.answers_count}</div>
                            <div>📝 Created: {new Date(log.created_at).toLocaleDateString()}</div>
                          </div>
                          {log.notes && (
                            <div className="mt-2 text-sm text-slate-600">
                              <strong>Notes:</strong> {log.notes.substring(0, 100)}
                              {log.notes.length > 100 && '...'}
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditLog(log)}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteReflectionLog(log.id, log.pupil_name, log.lesson_date)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {allReflectionLogs.length === 0 && (
                <Card className="text-center p-8">
                  <CardContent>
                    <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No reflection logs in system</h3>
                    <p className="text-gray-500">All logs have been cleared or none have been created yet</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Edit Reflection Log Dialog */}
          <Dialog open={editLogDialog} onOpenChange={setEditLogDialog}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Edit Reflection Log</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateLog} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="log-lesson-date">Lesson Date *</Label>
                  <MobileFriendlyDatePicker
                    selected={logFormData.lesson_date}
                    onSelect={(date) => setLogFormData({...logFormData, lesson_date: date})}
                    placeholder="Pick a date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="log-lesson-hours">Lesson Hours</Label>
                  <Input
                    id="log-lesson-hours"
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="8"
                    value={logFormData.lesson_hours}
                    onChange={(e) => setLogFormData({...logFormData, lesson_hours: parseFloat(e.target.value) || 1.0})}
                    placeholder="1.0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="log-notes">Notes</Label>
                  <Textarea
                    id="log-notes"
                    value={logFormData.notes}
                    onChange={(e) => setLogFormData({...logFormData, notes: e.target.value})}
                    placeholder="Enter lesson notes..."
                    rows={4}
                  />
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setEditLogDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    Update Log
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* All Questions Management Tab */}
          <TabsContent value="questions">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-3xl font-bold text-slate-800">All Questions Management</h2>
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                  {allQuestions.length} Total Questions
                </Badge>
              </div>

              <div className="space-y-4">
                {allQuestions.map((question, index) => (
                  <Card key={question.id}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <Badge variant="secondary">Q{index + 1}</Badge>
                            <Badge 
                              variant={question.question_type === 'scale' ? 'default' : 'outline'}
                              className={question.question_type === 'scale' ? 'bg-blue-600' : ''}
                            >
                              {question.question_type === 'scale' 
                                ? `Scale ${question.scale_min}-${question.scale_max}`
                                : 'Text Response'
                              }
                            </Badge>
                            <Badge variant={question.is_active ? 'default' : 'secondary'}>
                              {question.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <Badge variant="outline" className="bg-gray-50">
                              Used {question.usage_count} times
                            </Badge>
                          </div>
                          <p className="text-lg text-slate-800 mb-2">{question.text}</p>
                          <p className="text-xs text-slate-500">
                            Created: {new Date(question.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditQuestion(question)}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteQuestion(question.id, question.text)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {allQuestions.length === 0 && (
                <Card className="text-center p-8">
                  <CardContent>
                    <MessageSquare size={48} className="mx-auto text-gray-400 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No questions in system</h3>
                    <p className="text-gray-500">All questions have been deleted or none have been created yet</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Edit Question Dialog */}
          <Dialog open={editQuestionDialog} onOpenChange={setEditQuestionDialog}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Edit Question</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateQuestion} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="question-text">Question Text *</Label>
                  <Textarea
                    id="question-text"
                    value={questionFormData.text}
                    onChange={(e) => setQuestionFormData({...questionFormData, text: e.target.value})}
                    placeholder="Enter your reflection question..."
                    rows={3}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="question-type">Question Type *</Label>
                  <select
                    id="question-type"
                    value={questionFormData.question_type}
                    onChange={(e) => setQuestionFormData({...questionFormData, question_type: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="text">Text Response</option>
                    <option value="scale">Scale Rating</option>
                  </select>
                </div>

                {questionFormData.question_type === 'scale' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="question-scale-min">Scale Minimum</Label>
                      <Input
                        id="question-scale-min"
                        type="number"
                        value={questionFormData.scale_min}
                        onChange={(e) => setQuestionFormData({...questionFormData, scale_min: parseInt(e.target.value)})}
                        min="1"
                        max="9"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="question-scale-max">Scale Maximum</Label>
                      <Input
                        id="question-scale-max"
                        type="number"
                        value={questionFormData.scale_max}
                        onChange={(e) => setQuestionFormData({...questionFormData, scale_max: parseInt(e.target.value)})}
                        min="2"
                        max="10"
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setEditQuestionDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    Update Question
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* System Management Tab */}
          <TabsContent value="system">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-slate-800">System Management</h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Platform Statistics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span>Average Hours per Instructor</span>
                        <Badge>
                          {dashboardData?.system_stats?.total_instructors > 0 
                            ? Math.round((dashboardData?.system_stats?.total_hours || 0) / dashboardData?.system_stats?.total_instructors * 10) / 10
                            : 0}h
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Average Pupils per Instructor</span>
                        <Badge>
                          {dashboardData?.system_stats?.total_instructors > 0 
                            ? Math.round((dashboardData?.system_stats?.total_pupils || 0) / dashboardData?.system_stats?.total_instructors * 10) / 10
                            : 0}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Average Logs per Pupil</span>
                        <Badge>
                          {dashboardData?.system_stats?.total_pupils > 0 
                            ? Math.round((dashboardData?.system_stats?.total_logs || 0) / dashboardData?.system_stats?.total_pupils * 10) / 10
                            : 0}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>System Health</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span>Database Status</span>
                        <Badge variant="default" className="bg-green-600">Online</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>API Status</span>
                        <Badge variant="default" className="bg-green-600">Operational</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Total System Records</span>
                        <Badge>
                          {(dashboardData?.system_stats?.total_instructors || 0) + 
                           (dashboardData?.system_stats?.total_pupils || 0) + 
                           (dashboardData?.system_stats?.total_logs || 0)}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-red-600">⚠️ Dangerous System Operations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Button
                        variant="destructive"
                        onClick={handleClearAllLogs}
                        className="h-16 bg-red-600 hover:bg-red-700"
                      >
                        🗑️ Clear All Reflection Logs
                        <br />
                        <span className="text-xs">Deletes all logs system-wide</span>
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleClearAllPupils}
                        className="h-16 bg-red-800 hover:bg-red-900"
                      >
                        💥 Clear All Pupils & Logs
                        <br />
                        <span className="text-xs">Nuclear option - clears everything</span>
                      </Button>
                    </div>
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800">
                        <strong>Warning:</strong> These operations are irreversible and will permanently delete data across all instructors. 
                        Use with extreme caution and ensure you have backups if needed.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;